// src/Commoncontext/UsersContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import UsersApi from "../Api/UsersApi";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from '../firebaseConfig';

const UsersContext = createContext();

export const UsersProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  // USERS
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const USERS_CACHE_KEY = 'users_cache_etag_v1';
  const usersCache = {
    map: new Map(), // companyId -> { data, version, etag, fetchedAt }
    hydrate() {
      try {
        const raw = localStorage.getItem(USERS_CACHE_KEY);
        if (raw) this.map = new Map(Object.entries(JSON.parse(raw)));
      } catch {}
    },
    persist() {
      try {
        localStorage.setItem(USERS_CACHE_KEY, JSON.stringify(Object.fromEntries(this.map)));
      } catch {}
    },
  };

  // INVITES
  const [invites, setInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);

  useEffect(() => { 
    usersCache.hydrate(); 
  }, []);

  // Auth state -> hae kirjautuneen käyttäjän tiedot
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          setLoading(true);
          const userData = await UsersApi.fetchUserById(currentUser.uid);
          if (!userData || userData.aktiivinen === false) {
            await signOut(auth);
            window.location.href = "/login";
          } else {
            setUser(userData);
            await UsersApi.updateLastSigned(currentUser.uid);
          }
        } catch (error) {
          console.error("Virhe käyttäjätietojen haussa:", error);
          await signOut(auth);
          window.location.href = "/login";
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchUsers = useCallback(async (companyId) => {
    if (!companyId) return;
    // 1) näytä cache heti jos löytyy
    const entry = usersCache.map.get(companyId);
    if (entry) {
      setUsers(entry.data);
    }
    // 2) spinner vain jos ei cachea
    setLoading(!entry);
    try {
      // 3) tee conditional GET etagilla
      const resp = await UsersApi.fetchUsers(companyId, { etag: entry?.etag });
      if (resp.status === 304) {
        // ei muutoksia -> käytä cachea, lopeta
        return;
      }
      // 200 -> päivitä cache ja UI
      const newEntry = {
        data: resp.users,
        version: resp.version,
        etag: resp.etag,
        fetchedAt: Date.now(),
      };
      usersCache.map.set(companyId, newEntry);
      usersCache.persist();
      setUsers(resp.users);
    } catch (e) {
      console.error("Virhe haettaessa käyttäjiä:", e);
      // jos ei cachea, näytä tyhjä lista virhetilanteessa
      if (!entry) setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUserById = useCallback(async (userId, _companyId) => {
    setLoading(true);
    try {
      const u = await UsersApi.fetchUserById(userId);
      setSelectedUser(u);
    } catch (e) {
      console.error("Virhe haettaessa käyttäjää:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateUserRole = useCallback(async (userId, companyId, role, hasRole) => {
    try {
      await UsersApi.updateUserRole(userId, companyId, role, hasRole);
      await fetchUsers(companyId);
    } catch (e) {
      console.error("Virhe päivitettäessä käyttäjän roolia:", e);
    }
  }, [fetchUsers]);

  const updateUserActive = useCallback(async (userId, isActive) => {
    try {
      await UsersApi.updateUserActive(userId, isActive);
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, aktiivinen: isActive } : u)));
    } catch (e) {
      console.error("Virhe aktivoitaessa/inaktivoitaessa käyttäjää:", e);
    }
  }, []);

  const sendPasswordReset = useCallback(async (email) => {
    try {
      const res = await UsersApi.sendPasswordResetEmail(email);
      return res?.success;
    } catch (e) {
      console.error("Reset email error:", e);
      return false;
    }
  }, []);

const createUser = useCallback(async (companyId, { firstName, lastName, email, password, roles, aktiivinen = true }) => {
  try {
    // Backend luo Auth-käyttäjän, käyttäjät-dokumentin ja roolit
    const { uid } = await UsersApi.createUser({companyId, firstName, lastName, email, password, roles});
    // 🔹 Optimistinen lisäys local cacheen (ilman uutta fetchiä)
    // Rakennetaan vähintään ne kentät, joita UI käyttää UsersV2:ssä
    const optimisticUser = {
      id: uid,
      etunimi: firstName,
      sukunimi: lastName, 
      sähköposti: email,
      aktiivinen,
      roolit: roles,              // ['asukas','osakas',...]
      kirjautunut: null,          // ei vielä kirjautunut
      profiilikuvaurl: null,      // ei vielä kuvaa
    };
    setUsers(prev => {
      // vältä tuplat jos uid jo listassa
      if (prev?.some(u => u.id === uid)) return prev;
      return [...(prev || []), optimisticUser];
    });
    return uid;
  } catch (e) {
    // Heitä virhe eteenpäin, jotta UI näyttää syyn (email_in_use, weak_password, tms.)
    throw e;
  }
},[setUsers]);

  // ...
const fetchInvites = useCallback(async (companyId) => {
  if (!companyId) return;
  setInvitesLoading(true);
  try {
    const list = await UsersApi.fetchInvites(companyId);
    setInvites(list || []);
  } catch (e) {
    console.error("Virhe kutsuja haettaessa:", e);
  } finally {
    setInvitesLoading(false);
  }
}, []);

const createInvite = useCallback(async (companyId, { name, email, roles }) => {
  try {
    // Backend palauttaa ainakin { id, token }
    const res = await UsersApi.createInvite({ companyId, name, email, roles });
    // Laske voimassaolo 14 vrk päähän
    const expiresAtDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    // Rakennetaan optimistinen kutsu-objekti
    const optimisticInvite = {
      id: res.id,
      companyId,
      name,
      email,
      roles,
      status: 'pending',
      createdAt: { _seconds: Math.floor(Date.now() / 1000) },
      expiresAt: { _seconds: Math.floor(expiresAtDate.getTime() / 1000) },
    };
    // Päivitä invites cache
    setInvites(prev => {
      if (prev?.some(iv => iv.id === res.id)) return prev;
      return [optimisticInvite, ...(prev || [])];
    });
    return res.id;
  } catch (e) {
    console.error("Virhe kutsun luonnissa:", e);
    throw e;
  }
},[setInvites]);

const resendInvite = useCallback(async (inviteId) => {
  try {
    await UsersApi.resendInvite(inviteId);
    // Laske uusi voimassaolo (14 vrk tästä hetkestä)
    const newExpiresAt = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
    setInvites((prev) =>
      prev.map((iv) =>
        iv.id === inviteId
          ? {
              ...iv,
              createdAt: { _seconds: Math.floor(Date.now() / 1000), _nanoseconds: 0 },
              expiresAt: { _seconds: Math.floor(newExpiresAt.getTime() / 1000), _nanoseconds: 0 },
            }
          : iv
      )
    );
  } catch (e) {
    console.error("Virhe kutsun uudelleenlähetyksessä:", e);
    throw e;
  }
}, []);

const cancelInvite = useCallback(async (inviteId) => {
  await UsersApi.cancelInvite(inviteId);
  setInvites(prev => prev.filter(i => i.id !== inviteId)); // poista heti UI:sta
}, []);      

  return (
    <UsersContext.Provider value={{
      // auth’d user
      user, setUser,
      // users
      users, selectedUser, loading,
      fetchUsers, fetchUserById, updateUserRole, updateUserActive, sendPasswordReset, createUser,
      // invites
      invites, invitesLoading,
      fetchInvites, createInvite, resendInvite, cancelInvite,
    }}>
      {children}
    </UsersContext.Provider>
  );
};

export const useUsers = () => useContext(UsersContext);

